import CommonTips, { CommonTipsActionType, CommonTipsType } from '@com/common-tips';

import type { ISocketListener } from './socket-listener';
import { OnlineEditServices } from '@src/slide/domain/slide-app/scenes/online/core/online-edit-services.core';
import { RecoveryStatus } from '@tencent/slide-sdk-persistent';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import i18n from '@i18next/i18n';
import { network } from '@tencent/netcheck';
import once from 'lodash/once';

const recoveryTipsID = 'recovery-tips-id';
const blockCheckTipsID = 'block-check-tips-id';

export default class PersistentListener {
    private isRecovering = false;
    private tips: CommonTips;
    private activeBlockCheck = false;

    public constructor(private socketListener: ISocketListener) {
        this.tips = CommonTips.getInstance();
        this.registerPersistentEvent();
        this.registerBeforeunload();

        // SDK Persist 加载比较晚，所以这里自己取标志位。临时解决方案，比较戳的实现，勿喷，可联系 @rongodzhang
        // 需 src/slide/domain/slide-app/scenes/online/managers/online-edit-notice-manager.ts 联动配合
        SlideDataServices.DocInfoManager.promise().then(() => {
            if (this.hasOfflineData()) this.isRecovering = true;
        });
    }

    public unregisterBeforeunload = () => {
        window.removeEventListener('beforeunload', this.onbeforeUnloadCb);
    };


    private willRecoverPersistentData = () => {
        // CommonTips.getInstance().hideMask();
        if (this.isRecovering) return;
        const commonTipsConfig = {
            message: i18n.t('正在恢复数据，请耐心等待，整个过程可能会持续几分钟...'),
            commonTipsType: CommonTipsType.INFO,
            block: true,
            closable: false,
            id: recoveryTipsID,
        };
        CommonTips.getInstance().show(commonTipsConfig);
        this.isRecovering = true;
    };

    private didRecoverPersistentData = () => {
        // CommonTips.getInstance().hideMask();
        if (!this.isRecovering) return;
        CommonTips.getInstance().hideById(recoveryTipsID);
        this.isRecovering = false;
    };

    private openDataBaseError = () => {
        if (network.isOnline) return;
        this.socketListener.handleOnOffline();
    };

    private dataBaseFatalError = () => {
        this.startDetectBlocked();
        if (network.isOnline) return;
        this.socketListener.handleOnOffline();
    };


    private onbeforeUnloadCb = (e) => {
        this.guideToSaveSlideData(e);
    };


    private async startDetectBlocked() {
        if (this.activeBlockCheck) return;
        this.activeBlockCheck = true;
        this.reportDetectBlocked('start');
        const reportOnce = once(() => this.reportDetectBlocked('show'));

        const persistent = (await OnlineEditServices.CollaborationManager.promise()).getPersistent();
        let tIds = persistent.getMutationsPacks().map(m => m.taskId);
        setInterval(() => {
            const cTIds = persistent.getMutationsPacks().map(m => m.taskId);
            if (!cTIds.length || cTIds.length - tIds.length < 10) {
                tIds = cTIds;
                return;
            }
            this.tips.hideById(blockCheckTipsID, true);
            this.tips.show({
                id: blockCheckTipsID,
                message: i18n.t('网络连接错误，数据暂时无法保存，请勿关闭页面'),
                block: true,
                commonTipsType: CommonTipsType.ERROR,
                actionType: CommonTipsActionType.HOWTODEAL,
                howToDetailConfig: {
                    disableRefresh: true,
                },
            });
            reportOnce();
        }, 60000);
    }

    private reportDetectBlocked(stage: 'start' | 'show') {
        const logger = SlideDataServices.Infra.Logger();
        logger?.monitor?.reportStatus?.('detectBlock', 1, '', [`${stage}-${network.isOnline ? 'online' : 'offline'}`]);
    }

    /**
     * 当内存有未提交的数据/不支持离线编辑/PC的环境下，引导用户保存数据
     * @param e
     */
    private async guideToSaveSlideData(e) {
        const persistent = OnlineEditServices.CollaborationManager()?.getPersistent();
        if (!persistent) return;
        persistent?.onbeforeUnload();
        const hasUnsentChanges = !!persistent.getMutationsPacks().length;
        if (hasUnsentChanges
            && [RecoveryStatus.None, RecoveryStatus.DidRecoverData].includes(persistent.getRecoveryStatus())) {
            if (hasUnsentChanges) {
                this.setDataLossFlag(!persistent.supportOfflineEdit);
                e.preventDefault();
                e.returnValue = '';
            } else {
                this.setDataLossFlag(false);
            }
            return;
        }
    }

    private registerBeforeunload = () => {
        // 关闭页面时，统计内存中未提交的数据
        window.addEventListener('beforeunload', this.onbeforeUnloadCb);
    };

    private registerPersistentEvent() {
        SlideDataServices.SDKDataServices.EventManager.promise().then(({ persistentEvents }) => {
            persistentEvents?.onDataBaseFatalError(this.dataBaseFatalError);
            persistentEvents?.onOpenDataBaseError(this.openDataBaseError);
        });
        OnlineEditServices.CollaborationManager.promise().then((collaborationManager) => {
            // collaborationManager.getPersistent().onBeforeGetRecoveryData(this.beforeGetRecoveryData);
            collaborationManager.getPersistent().onNeedRecoveryData(this.willRecoverPersistentData);
            collaborationManager.getPersistent().onDidRecoveryData(this.didRecoverPersistentData);
            collaborationManager.onCollaborationReady(() => {
                const flag = this.getDataLossFlag();
                if (flag === 'true') {
                    this.tips.show({
                        message: i18n.t('检测到缓存失败，可能有尚未同步到云端的数据'),
                        block: false,
                        closable: true,
                        commonTipsType: CommonTipsType.INFO,
                        actionType: CommonTipsActionType.HOWTODEAL,
                        howToDetailConfig: {
                            disableRefresh: true,
                        },
                        closeCallback: () => this.setDataLossFlag(false),
                        callBack: (actionType: number, fn?: any) => {
                            if (actionType === CommonTipsActionType.CREATE_COPY_DOC) {
                                fn(false);
                            }
                        },
                    });
                }
            });
        });
    }

    private setDataLossFlag(isLoss: boolean) {
        localStorage.setItem('slide-data-loss', isLoss.toString());
    }

    private getDataLossFlag() {
        return localStorage.getItem('slide-data-loss');
    }

    private hasOfflineData(): boolean {
        const globalPadId = SlideDataServices.DocInfoManager()?.globalPadId;
        const uid = SlideDataServices.DocInfoManager()?.userId;
        return localStorage.getItem(`${globalPadId}-${uid}-has-offline-data`) === 'true';
    }
}
